import PropTypes from "prop-types"
import React, { useEffect, useState, useCallback, useRef } from "react"
import axios from "axios"
import { Link } from "react-router-dom"
import { getCurrentUser } from "../../helpers/Utils"
import CallRecordingPlayButton from "../Common/CallRecordingPlayButton"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap"

const MUTED = "#495057"
const BODY = "#212529"
const LABEL_BG = "#dee2e6"
const PER_PAGE = 15

const DIRECTION_LABELS = {
  incoming: "Incoming",
  outgoing: "Outgoing",
  missed: "Missed",
  not_picked: "Not picked",
}

const DIRECTION_SUMMARY_KEYS = {
  incoming: "incoming_calls",
  outgoing: "outgoing_calls",
  missed: "missed_calls",
  not_picked: "not_picked_calls",
}

const DIRECTION_FILTER_OPTIONS = [
  { key: null, label: "All" },
  { key: "incoming", label: "Incoming" },
  { key: "outgoing", label: "Outgoing" },
  { key: "missed", label: "Missed" },
  { key: "not_picked", label: "Not picked" },
]

/** High-contrast pill (avoids theme Badge colors that wash out on striped rows) */
const Pill = ({ children, bg, color, border }) => (
  <span
    className="d-inline-block me-1 mb-1"
    style={{
      padding: "2px 10px",
      borderRadius: "999px",
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.02em",
      backgroundColor: bg,
      color,
      border: border ? `1px solid ${border}` : "none",
      lineHeight: 1.4,
    }}
  >
    {children}
  </span>
)

function formatDuration(seconds) {
  const s = Number(seconds) || 0
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  const pad = n => String(n).padStart(2, "0")
  return `${pad(h)}:${pad(m)}:${pad(r)}`
}

/** "2026-05-14 15:50:03" → "14-May 15:50" for compact activity column */
function shortActivity(s) {
  if (!s || typeof s !== "string") {
    return "—"
  }
  const [d, t] = s.split(" ")
  if (!d || !t) {
    return s
  }
  const [Y, M, day] = d.split("-")
  const mon = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][Number(M)] || M
  return `${day}-${mon} ${t.slice(0, 5)}`
}

/** Full date/time for expanded per-call rows (higher contrast than shortActivity). */
function formatExpandedDateTime(s) {
  if (!s || typeof s !== "string") {
    return "—"
  }
  const normalized = s.includes("T") ? s : s.replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, "$1T$2")
  const dt = new Date(normalized)
  if (Number.isNaN(dt.getTime())) {
    return s
  }
  return dt.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

/** Split date / time for clearer hierarchy in expanded rows. */
function formatExpandedDateParts(s) {
  if (!s || typeof s !== "string") {
    return { dateLine: "—", timeLine: "" }
  }
  const normalized = s.includes("T") ? s : s.replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, "$1T$2")
  const dt = new Date(normalized)
  if (Number.isNaN(dt.getTime())) {
    return { dateLine: s, timeLine: "" }
  }
  const dateLine = dt.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  })
  const timeLine = dt.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })
  return { dateLine, timeLine }
}

const NESTED = {
  wrapBg: "#f8fafc",
  cardBg: "#ffffff",
  border: "#e2e8f0",
  theadBg: "#f1f5f9",
  theadColor: "#64748b",
  rowDivider: "#e2e8f0",
  shadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
}

const DIRECTION_PILL = {
  incoming: {
    bg: "#ecfdf5",
    color: "#047857",
    border: "#a7f3d0",
    icon: "fa-arrow-down",
    label: "Incoming",
  },
  outgoing: {
    bg: "#eff6ff",
    color: "#1d4ed8",
    border: "#bfdbfe",
    icon: "fa-arrow-up",
    label: "Outgoing",
  },
  missed: {
    bg: "#fffbeb",
    color: "#b45309",
    border: "#fde68a",
    icon: "fa-phone-slash",
    label: "Missed",
  },
  not_picked: {
    bg: "#fef2f2",
    color: "#b91c1c",
    border: "#fecaca",
    icon: "fa-exclamation-circle",
    label: "Not picked",
  },
}

function CallTypeChip({ pill }) {
  return (
    <span
      className="d-inline-flex align-items-center gap-2"
      style={{
        padding: "6px 12px",
        borderRadius: "8px",
        fontSize: "0.8125rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
        backgroundColor: pill.bg,
        color: pill.color,
        border: `1px solid ${pill.border}`,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
      }}
    >
      <i
        className={`fas ${pill.icon}`}
        style={{ fontSize: "0.75rem", opacity: 0.92 }}
        aria-hidden
      />
      <span>{pill.label}</span>
    </span>
  )
}
CallTypeChip.propTypes = {
  pill: PropTypes.object.isRequired,
}

function DurationCell({ value }) {
  return (
    <span
      className="font-monospace d-inline-block"
      style={{
        fontSize: "0.875rem",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "0.02em",
        color: "#0f172a",
        padding: "6px 10px",
        backgroundColor: "#f8fafc",
        borderRadius: "6px",
        border: `1px solid ${NESTED.border}`,
      }}
    >
      {value}
    </span>
  )
}
DurationCell.propTypes = {
  value: PropTypes.string.isRequired,
}

/** Device CRM notes + follow-up message; status stays in Follow-up column only. */
function CallLogNotesCell({ call, followup }) {
  const deviceNotes =
    call.conversation_notes && String(call.conversation_notes).trim()
  const followNote =
    followup?.note_snippet && String(followup.note_snippet).trim()
  const hasAny = deviceNotes || followNote

  if (!hasAny) {
    return (
      <span
        style={{
          color: "#94a3b8",
          fontSize: "0.8125rem",
          fontStyle: "italic",
        }}
      >
        No notes
      </span>
    )
  }

  const blockTitle = {
    fontSize: "0.65rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: NESTED.theadColor,
    marginBottom: "0.35rem",
  }
  const blockBody = {
    color: "#334155",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  }

  return (
    <div className="d-flex flex-column gap-2">
      {deviceNotes ? (
        <div
          className="rounded-3 p-2"
          style={{
            backgroundColor: "#fafbfc",
            border: `1px solid ${NESTED.border}`,
          }}
        >
          <div style={blockTitle}>Device / call notes</div>
          <div style={blockBody}>{call.conversation_notes}</div>
        </div>
      ) : null}
      {followNote ? (
        <div
          className="rounded-3 p-2"
          style={{
            backgroundColor: "#fffbeb",
            border: `1px solid #fde68a`,
            borderLeftWidth: "3px",
            borderLeftColor: "#d97706",
          }}
        >
          <div style={blockTitle}>Follow-up note</div>
          <div style={blockBody}>{followup.note_snippet}</div>
        </div>
      ) : null}
    </div>
  )
}
CallLogNotesCell.propTypes = {
  call: PropTypes.object.isRequired,
  followup: PropTypes.object,
}

function leadTooltipId(groupKey) {
  return `lead-tip-${String(groupKey || "x").replace(/[^a-zA-Z0-9_-]/g, "_")}`
}

const CallLogHistoryDetailModal = ({
  isOpen,
  toggle,
  summaryLog,
  filterStartDate,
  filterEndDate,
  directionFilter = null,
  onDirectionFilterChange,
}) => {
  const staffName = summaryLog?.user?.name || "Staff"
  const userId = summaryLog?.id ?? summaryLog?.user?.id

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [expandedKeys, setExpandedKeys] = useState({})
  const [callDetailsByGroup, setCallDetailsByGroup] = useState({})
  const fetchSeq = useRef(0)

  const fetchHistory = useCallback(
    page => {
      if (userId == null) {
        return
      }
      const seq = ++fetchSeq.current
      const safePage = Math.max(1, parseInt(page, 10) || 1)
      setCurrentPage(safePage)
      setLoading(true)
      setError("")

      let url = `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
        "company_name"
      )}/crm-call-history?user_id=${userId}&per_page=${PER_PAGE}&page=${safePage}`

      if (filterStartDate && filterEndDate) {
        url += `&start_date=${filterStartDate}&end_date=${filterEndDate}`
      }

      if (directionFilter) {
        url += `&direction=${encodeURIComponent(directionFilter)}`
      }

      axios
        .get(url, {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${getCurrentUser()?.token}`,
          },
        })
        .then(res => {
          if (seq !== fetchSeq.current) {
            return
          }
          if (res.data.status) {
            const d = res.data.data
            setRows(d.data || [])
            setLastPage(Math.max(1, parseInt(d.last_page, 10) || 1))
            setCurrentPage(Math.max(1, parseInt(d.current_page, 10) || 1))
            setTotal(parseInt(d.total, 10) || 0)
          } else {
            setRows([])
            setError(res.data.message || "Could not load call history")
          }
        })
        .catch(err => {
          if (seq !== fetchSeq.current) {
            return
          }
          setRows([])
          const msg =
            err.response?.data?.message ||
            (err.response?.status === 403
              ? "You are not allowed to view this user’s call history."
              : "Failed to fetch CRM call history")
          setError(msg)
        })
        .finally(() => {
          if (seq === fetchSeq.current) {
            setLoading(false)
          }
        })
    },
    [userId, filterStartDate, filterEndDate, directionFilter]
  )

  const fetchGroupCalls = useCallback(
    async row => {
      const groupKey =
        row.group_key || `${row.phone_number}-${row.call_date}`
      setCallDetailsByGroup(prev => ({
        ...prev,
        [groupKey]: { loading: true, calls: prev[groupKey]?.calls || [] },
      }))

      let url = `${process.env.REACT_APP_API_URL}/${localStorage.getItem(
        "company_name"
      )}/crm-call-history/calls?user_id=${userId}&phone_number=${encodeURIComponent(
        row.phone_number
      )}&call_date=${encodeURIComponent(row.call_date)}`

      if (directionFilter) {
        url += `&direction=${encodeURIComponent(directionFilter)}`
      }

      try {
        const res = await axios.get(url, {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${getCurrentUser()?.token}`,
          },
        })
        if (res.data.status) {
          setCallDetailsByGroup(prev => ({
            ...prev,
            [groupKey]: {
              loading: false,
              calls: res.data.data?.calls || [],
            },
          }))
        } else {
          setCallDetailsByGroup(prev => ({
            ...prev,
            [groupKey]: { loading: false, calls: [], error: res.data.message },
          }))
        }
      } catch (err) {
        setCallDetailsByGroup(prev => ({
          ...prev,
          [groupKey]: {
            loading: false,
            calls: [],
            error:
              err.response?.data?.message || "Could not load call details",
          },
        }))
      }
    },
    [userId, directionFilter]
  )

  const toggleRowExpand = useCallback(
    row => {
      const groupKey =
        row.group_key || `${row.phone_number}-${row.call_date}`
      if (expandedKeys[groupKey]) {
        setExpandedKeys(prev => ({ ...prev, [groupKey]: false }))
        return
      }
      setExpandedKeys(prev => ({ ...prev, [groupKey]: true }))
      const cached = callDetailsByGroup[groupKey]
      if (!cached || (!cached.loading && !cached.calls?.length && !cached.error)) {
        fetchGroupCalls(row)
      }
    },
    [expandedKeys, callDetailsByGroup, fetchGroupCalls]
  )

  useEffect(() => {
    if (!isOpen || !summaryLog || userId == null) {
      return
    }
    setCurrentPage(1)
    fetchHistory(1)
  }, [
    isOpen,
    summaryLog,
    userId,
    filterStartDate,
    filterEndDate,
    directionFilter,
    fetchHistory,
  ])

  useEffect(() => {
    if (!isOpen) {
      setRows([])
      setError("")
      setTotal(0)
      setLastPage(1)
      setCurrentPage(1)
      setExpandedKeys({})
      setCallDetailsByGroup({})
    }
  }, [isOpen])

  if (!summaryLog) {
    return null
  }

  const page = Number(currentPage) || 1
  const last = Number(lastPage) || 1
  const canPrev = page > 1 && !loading
  const canNext = page < last && !loading
  const showPager = !error && total > 0

  const filterLabel = directionFilter
    ? DIRECTION_LABELS[directionFilter] || directionFilter
    : null

  const headerCount = directionFilter
    ? summaryLog[DIRECTION_SUMMARY_KEYS[directionFilter]] ?? 0
    : summaryLog.total_calls ?? 0

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      scrollable
      className="call-history-detail-modal"
      contentClassName="border-0 shadow"
    >
      <ModalHeader toggle={toggle} className="border-bottom bg-white">
        <div>
          <div className="fw-semibold" style={{ color: BODY, fontSize: "1.1rem" }}>
            Call history (device)
            {filterLabel ? (
              <span
                className="ms-2 badge rounded-pill"
                style={{
                  backgroundColor: "#e7f1ff",
                  color: "#0d47a1",
                  fontSize: "0.75rem",
                  verticalAlign: "middle",
                }}
              >
                {filterLabel} only
              </span>
            ) : null}
          </div>
          <div
            className="mt-1"
            style={{ color: MUTED, fontSize: "0.875rem", fontWeight: 500 }}
          >
            {staffName}
            {userId != null ? ` · User #${userId}` : ""}
            {summaryLog?.date ? ` · ${summaryLog.date}` : ""}
            {filterLabel ? ` · Showing ${filterLabel.toLowerCase()} calls` : ""}
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="pt-3 bg-white">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span
            className="small text-uppercase me-1"
            style={{ color: "#334155", fontWeight: 700, letterSpacing: "0.06em" }}
          >
            Filter:
          </span>
          {DIRECTION_FILTER_OPTIONS.map(({ key, label }) => {
            const isActive =
              key === null
                ? directionFilter == null || directionFilter === ""
                : directionFilter === key
            return (
              <button
                key={label}
                type="button"
                className="btn btn-sm"
                onClick={() => onDirectionFilterChange?.(key)}
                style={
                  isActive
                    ? {
                        backgroundColor: "#f36c23",
                        border: "1px solid #f36c23",
                        color: "#fff",
                        fontWeight: 600,
                      }
                    : {
                        backgroundColor: "#fff",
                        border: "1px solid #64748b",
                        color: "#1e293b",
                        fontWeight: 600,
                      }
                }
              >
                {label}
              </button>
            )
          })}
        </div>
        <Row className="g-2 mb-3">
          <Col md={2} sm={4} xs={6}>
            <div
              className="rounded-3 p-3 text-center"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${NESTED.border}`,
                boxShadow: NESTED.shadow,
              }}
            >
              <div
                className="small text-uppercase"
                style={{ color: NESTED.theadColor, fontWeight: 700, letterSpacing: "0.06em" }}
              >
                {filterLabel ? `${filterLabel} calls` : "Total calls"}
              </div>
              <div className="fs-4 fw-bold mt-1" style={{ color: "#1d4ed8" }}>
                {headerCount}
              </div>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div
              className="rounded-3 p-3 text-center"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${NESTED.border}`,
                boxShadow: NESTED.shadow,
              }}
            >
              <div
                className="small text-uppercase"
                style={{ color: NESTED.theadColor, fontWeight: 700, letterSpacing: "0.06em" }}
              >
                Incoming
              </div>
              <div className="fs-4 fw-bold mt-1" style={{ color: "#047857" }}>
                {summaryLog.incoming_calls ?? 0}
              </div>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div
              className="rounded-3 p-3 text-center"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${NESTED.border}`,
                boxShadow: NESTED.shadow,
              }}
            >
              <div
                className="small text-uppercase"
                style={{ color: NESTED.theadColor, fontWeight: 700, letterSpacing: "0.06em" }}
              >
                Outgoing
              </div>
              <div className="fs-4 fw-bold mt-1" style={{ color: "#1d4ed8" }}>
                {summaryLog.outgoing_calls ?? 0}
              </div>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div
              className="rounded-3 p-3 text-center"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${NESTED.border}`,
                boxShadow: NESTED.shadow,
              }}
            >
              <div
                className="small text-uppercase"
                style={{ color: NESTED.theadColor, fontWeight: 700, letterSpacing: "0.06em" }}
              >
                Missed
              </div>
              <div className="fs-4 fw-bold mt-1" style={{ color: "#b45309" }}>
                {summaryLog.missed_calls ?? 0}
              </div>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div
              className="rounded-3 p-3 text-center"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${NESTED.border}`,
                boxShadow: NESTED.shadow,
              }}
            >
              <div
                className="small text-uppercase"
                style={{ color: NESTED.theadColor, fontWeight: 700, letterSpacing: "0.06em" }}
              >
                Not picked
              </div>
              <div className="fs-4 fw-bold mt-1" style={{ color: "#b91c1c" }}>
                {summaryLog.not_picked_calls ?? 0}
              </div>
            </div>
          </Col>
          <Col md={2} sm={4} xs={6}>
            <div
              className="rounded-3 p-3 text-center"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${NESTED.border}`,
                boxShadow: NESTED.shadow,
              }}
            >
              <div
                className="small text-uppercase"
                style={{ color: NESTED.theadColor, fontWeight: 700, letterSpacing: "0.06em" }}
              >
                Total duration
              </div>
              <div className="fs-5 fw-bold mt-1 font-monospace" style={{ color: "#0f172a" }}>
                {summaryLog.total_duration_formatted || "00:00:00"}
              </div>
            </div>
          </Col>
        </Row>

        {!loading && !error && total > 0 && (
          <div className="d-flex justify-content-end mb-2">
            <Badge
              pill
              className="align-self-center border"
              style={{
                backgroundColor: "#e7f1ff",
                color: "#0d47a1",
                borderColor: "#90caf9",
                fontWeight: 600,
              }}
            >
              {total} group{total === 1 ? "" : "s"}
            </Badge>
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2 small mb-2" role="alert">
            {error}
          </div>
        )}

        {loading && rows.length === 0 && !error && (
          <div className="d-flex align-items-center gap-2 text-secondary small py-4 justify-content-center">
            <Spinner size="sm" style={{ color: "#0d6efd" }} />
            <span style={{ color: MUTED, fontWeight: 500 }}>Loading…</span>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="small py-3" style={{ color: MUTED }}>
            {directionFilter
              ? `No ${filterLabel?.toLowerCase() || directionFilter} calls in this range for this user.`
              : "No synced call rows for this range. They appear after the mobile app syncs call details for this user."}
          </div>
        )}

        {(rows.length > 0 || (loading && rows.length > 0)) && (
          <div
            className="table-responsive call-history-table-wrap"
            style={{
              touchAction: "manipulation",
              borderRadius: "12px",
              maxHeight: "min(52vh, 440px)",
              overflow: "auto",
              border: `1px solid ${NESTED.border}`,
              backgroundColor: "#fff",
              boxShadow: NESTED.shadow,
            }}
          >
            <Table
              hover
              borderless
              size="sm"
              className="mb-0 align-middle call-history-detail-table"
              style={{
                color: "#212529",
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
            >
              <thead
                className="small text-uppercase"
                style={{
                  backgroundColor: NESTED.theadBg,
                  color: NESTED.theadColor,
                  borderBottom: `1px solid ${NESTED.border}`,
                }}
              >
                <tr>
                  <th className="py-3 ps-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Day
                  </th>
                  <th className="py-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Contact
                  </th>
                  <th className="py-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Number
                  </th>
                  <th className="py-3 text-center fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Calls
                  </th>
                  <th className="py-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Picked
                  </th>
                  <th className="py-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Missed / NP
                  </th>
                  <th className="py-3 text-end pe-2 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Duration
                  </th>
                  <th className="py-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Last / first
                  </th>
                  <th className="py-3 pe-3 fw-semibold" style={{ letterSpacing: "0.05em" }}>
                    Lead
                  </th>
                </tr>
              </thead>
              <tbody className="small" style={{ verticalAlign: "middle" }}>
                {rows.map(row => {
                  const inC = row.incoming_count ?? 0
                  const outC = row.outgoing_count ?? 0
                  const picked = row.picked_count ?? 0
                  const miss = row.missed_count ?? 0
                  const np = row.not_picked_count ?? 0
                  const groupKey =
                    row.group_key || `${row.phone_number}-${row.call_date}`
                  const isExpanded = !!expandedKeys[groupKey]
                  const detail = callDetailsByGroup[groupKey]
                  const followupHint =
                    row.latest_followup_message || row.lead_last_message
                  const tipId = leadTooltipId(groupKey)

                  return (
                    <React.Fragment key={groupKey}>
                    <tr
                      style={{
                        color: "#212529",
                        borderBottom: `1px solid ${NESTED.rowDivider}`,
                      }}
                    >
                      <td
                        className="ps-3 text-nowrap fw-medium"
                        style={{ color: "#212529" }}
                      >
                        {row.call_date}
                      </td>
                      <td className="fw-medium" style={{ color: "#212529" }}>
                        {row.contact_name || "—"}
                      </td>
                      <td>
                        <span
                          className="font-monospace px-2 py-1 rounded-1"
                          style={{
                            backgroundColor: "#f8f9fa",
                            color: "#212529",
                            border: "1px solid #dee2e6",
                            fontSize: "0.8rem",
                          }}
                        >
                          {row.phone_number}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-decoration-none d-inline-flex align-items-center justify-content-center gap-1"
                          style={{ color: "#212529" }}
                          onClick={() => toggleRowExpand(row)}
                          aria-expanded={isExpanded}
                          title={isExpanded ? "Hide calls" : "Show each call"}
                        >
                          <span
                            className="d-inline-block fw-bold"
                            style={{
                              minWidth: "2.1rem",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              backgroundColor: "#212529",
                              color: "#ffffff",
                              fontSize: "0.85rem",
                            }}
                          >
                            {row.call_count}
                          </span>
                          <i
                            className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                            style={{ fontSize: "0.75rem", color: "#495057" }}
                            aria-hidden="true"
                          />
                        </button>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <div style={{ color: "#212529", fontWeight: 600 }}>
                            Picked{" "}
                            <span style={{ color: "#146c43" }}>{picked}</span>
                          </div>
                          <div style={{ color: "#495057", fontSize: "0.72rem" }}>
                            In{" "}
                            <strong style={{ color: "#212529" }}>{inC}</strong>{" "}
                            · Out{" "}
                            <strong style={{ color: "#212529" }}>{outC}</strong>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          {miss > 0 && (
                            <Pill bg="#fff3cd" color="#664d03" border="#ffc107">
                              Missed {miss}
                            </Pill>
                          )}
                          {np > 0 && (
                            <Pill bg="#f8d7da" color="#842029" border="#f5c2c7">
                              NP {np}
                            </Pill>
                          )}
                          {miss === 0 && np === 0 && (
                            <span style={{ color: MUTED }}>—</span>
                          )}
                        </div>
                      </td>
                      <td
                        className="text-end pe-2 font-monospace fw-semibold"
                        style={{ color: "#212529" }}
                      >
                        {row.total_duration_formatted ||
                          formatDuration(row.total_duration_seconds)}
                      </td>
                      <td style={{ color: BODY, fontSize: "0.8rem" }}>
                        <div className="d-flex align-items-baseline flex-wrap gap-1">
                          <span
                            style={{
                              display: "inline-block",
                              backgroundColor: LABEL_BG,
                              color: BODY,
                              padding: "2px 7px",
                              borderRadius: "4px",
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              letterSpacing: "0.04em",
                            }}
                          >
                            LAST
                          </span>
                          <strong style={{ color: BODY }}>
                            {shortActivity(row.last_started_at)}
                          </strong>
                        </div>
                        {row.call_count > 1 && row.first_started_at && (
                          <div className="d-flex align-items-baseline flex-wrap gap-1 mt-2">
                            <span
                              style={{
                                display: "inline-block",
                                backgroundColor: LABEL_BG,
                                color: BODY,
                                padding: "2px 7px",
                                borderRadius: "4px",
                                fontSize: "0.65rem",
                                fontWeight: 800,
                                letterSpacing: "0.04em",
                              }}
                            >
                              FIRST
                            </span>
                            <strong style={{ color: BODY }}>
                              {shortActivity(row.first_started_at)}
                            </strong>
                          </div>
                        )}
                      </td>
                      <td className="pe-3" style={{ color: BODY }}>
                        {row.lead_id ? (
                          <>
                            <Link
                              id={tipId}
                              to={`/leads?id=${row.lead_id}&global_search=1`}
                              className="text-break d-inline-block"
                              style={{
                                color: "#0a58ca",
                                fontWeight: 600,
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                                cursor: followupHint ? "help" : "pointer",
                              }}
                              onClick={() => toggle()}
                            >
                              {row.lead_name || `Lead #${row.lead_id}`}
                            </Link>
                            {followupHint ? (
                              <UncontrolledTooltip
                                placement="left"
                                target={tipId}
                                style={{ maxWidth: "320px", textAlign: "left" }}
                              >
                                <div className="small fw-semibold mb-1">
                                  Latest follow-up
                                </div>
                                <div style={{ whiteSpace: "pre-wrap" }}>
                                  {followupHint}
                                </div>
                              </UncontrolledTooltip>
                            ) : null}
                          </>
                        ) : (
                          <span style={{ color: MUTED }}>—</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr style={{ backgroundColor: NESTED.wrapBg }}>
                        <td colSpan={9} className="ps-3 pe-3 py-3">
                          {detail?.loading ? (
                            <div className="small d-flex align-items-center gap-2 text-secondary">
                              <Spinner size="sm" />
                              Loading calls…
                            </div>
                          ) : detail?.error ? (
                            <div className="small text-danger">{detail.error}</div>
                          ) : detail?.calls?.length ? (
                            <div
                              className="mb-0 rounded-3 overflow-hidden"
                              style={{
                                backgroundColor: NESTED.cardBg,
                                border: `1px solid ${NESTED.border}`,
                                boxShadow: NESTED.shadow,
                              }}
                            >
                              <div
                                className="px-3 py-2 small fw-semibold"
                                style={{
                                  backgroundColor: NESTED.theadBg,
                                  color: NESTED.theadColor,
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                }}
                              >
                                Call log details
                              </div>
                              <div className="table-responsive">
                                <table
                                  className="table table-sm mb-0 align-middle"
                                  style={{
                                    borderCollapse: "separate",
                                    borderSpacing: 0,
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        backgroundColor: "#fafbfc",
                                        borderBottom: `1px solid ${NESTED.border}`,
                                      }}
                                    >
                                      <th
                                        className="py-2 ps-3 fw-semibold small"
                                        style={{
                                          color: NESTED.theadColor,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                          fontSize: "0.65rem",
                                          borderBottom: `1px solid ${NESTED.border}`,
                                        }}
                                      >
                                        When
                                      </th>
                                      <th
                                        className="py-2 fw-semibold small"
                                        style={{
                                          color: NESTED.theadColor,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                          fontSize: "0.65rem",
                                          borderBottom: `1px solid ${NESTED.border}`,
                                        }}
                                      >
                                        Type
                                      </th>
                                      <th
                                        className="py-2 text-end pe-2 fw-semibold small"
                                        style={{
                                          color: NESTED.theadColor,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                          fontSize: "0.65rem",
                                          borderBottom: `1px solid ${NESTED.border}`,
                                        }}
                                      >
                                        Duration
                                      </th>
                                      <th
                                        className="py-2 fw-semibold small"
                                        style={{
                                          color: NESTED.theadColor,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                          fontSize: "0.65rem",
                                          borderBottom: `1px solid ${NESTED.border}`,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Recording
                                      </th>
                                      <th
                                        className="py-2 fw-semibold small"
                                        style={{
                                          color: NESTED.theadColor,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                          fontSize: "0.65rem",
                                          borderBottom: `1px solid ${NESTED.border}`,
                                        }}
                                      >
                                        Follow-up
                                      </th>
                                      <th
                                        className="py-2 pe-3 fw-semibold small"
                                        style={{
                                          color: NESTED.theadColor,
                                          letterSpacing: "0.04em",
                                          textTransform: "uppercase",
                                          fontSize: "0.65rem",
                                          borderBottom: `1px solid ${NESTED.border}`,
                                        }}
                                      >
                                        Notes
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.calls.map((call, callIdx) => {
                                      const pill =
                                        DIRECTION_PILL[call.direction] || {
                                          bg: "#f1f5f9",
                                          color: "#334155",
                                          border: "#cbd5e1",
                                          icon: "fa-question",
                                          label: call.direction,
                                        }
                                      const dur =
                                        call.duration_formatted ||
                                        formatDuration(call.duration_seconds)
                                      const fu = call.followup_after_call
                                      const { dateLine, timeLine } =
                                        formatExpandedDateParts(call.started_at)
                                      const isLast =
                                        callIdx === detail.calls.length - 1
                                      return (
                                        <tr
                                          key={call.id}
                                          style={{
                                            borderBottom: isLast
                                              ? "none"
                                              : `1px solid ${NESTED.rowDivider}`,
                                          }}
                                        >
                                          <td
                                            className="ps-3 py-3"
                                            style={{ verticalAlign: "middle" }}
                                          >
                                            <div
                                              style={{
                                                fontWeight: 600,
                                                fontSize: "0.8125rem",
                                                color: "#0f172a",
                                                lineHeight: 1.35,
                                              }}
                                            >
                                              {dateLine}
                                            </div>
                                            {timeLine ? (
                                              <div
                                                className="font-monospace mt-1"
                                                style={{
                                                  fontSize: "0.875rem",
                                                  fontWeight: 600,
                                                  fontVariantNumeric:
                                                    "tabular-nums",
                                                  color: "#334155",
                                                }}
                                              >
                                                {timeLine}
                                              </div>
                                            ) : null}
                                          </td>
                                          <td
                                            className="py-3"
                                            style={{ verticalAlign: "middle" }}
                                          >
                                            <CallTypeChip pill={pill} />
                                          </td>
                                          <td
                                            className="text-end pe-2 py-3"
                                            style={{ verticalAlign: "middle" }}
                                          >
                                            <DurationCell value={dur} />
                                          </td>
                                          <td
                                            className="py-3"
                                            style={{ verticalAlign: "middle" }}
                                          >
                                            <CallRecordingPlayButton
                                              url={
                                                call.has_recording === false
                                                  ? null
                                                  : call.recording_url || null
                                              }
                                              status={call.recording_status}
                                            />
                                          </td>
                                          <td
                                            className="py-3"
                                            style={{
                                              maxWidth: "16rem",
                                              verticalAlign: "middle",
                                            }}
                                          >
                                            {fu ? (
                                              <div
                                                className="rounded-3 p-2"
                                                style={{
                                                  backgroundColor: "#f8fafc",
                                                  border: `1px solid ${NESTED.border}`,
                                                  borderLeftWidth: "3px",
                                                  borderLeftColor: "#3b82f6",
                                                }}
                                              >
                                                <div
                                                  className="d-flex flex-wrap align-items-center gap-2"
                                                >
                                                  <span
                                                    className="small fw-semibold text-break"
                                                    style={{
                                                      color: "#0f172a",
                                                      lineHeight: 1.35,
                                                    }}
                                                  >
                                                    {fu.label}
                                                  </span>
                                                  {fu.type ? (
                                                    <span
                                                      className="badge rounded-pill"
                                                      style={{
                                                        backgroundColor:
                                                          "#e0e7ff",
                                                        color: "#3730a3",
                                                        fontSize: "0.65rem",
                                                        fontWeight: 700,
                                                      }}
                                                    >
                                                      {String(fu.type).replace(
                                                        /_/g,
                                                        " "
                                                      )}
                                                    </span>
                                                  ) : null}
                                                </div>
                                                {fu.logged_at ? (
                                                  <div
                                                    className="mt-1"
                                                    title="When this activity was recorded in CRM (not the device call time)."
                                                    style={{
                                                      color: "#64748b",
                                                      fontSize: "0.72rem",
                                                      fontWeight: 500,
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: 600 }}>
                                                      Recorded in CRM
                                                    </span>
                                                    <span
                                                      style={{
                                                        margin: "0 0.35rem",
                                                        color: "#94a3b8",
                                                      }}
                                                      aria-hidden
                                                    >
                                                      ·
                                                    </span>
                                                    {formatExpandedDateTime(
                                                      fu.logged_at
                                                    )}
                                                  </div>
                                                ) : null}
                                              </div>
                                            ) : (
                                              <span
                                                style={{
                                                  color: "#94a3b8",
                                                  fontSize: "0.8125rem",
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                No follow-up logged
                                              </span>
                                            )}
                                          </td>
                                          <td
                                            className="pe-3 py-3"
                                            style={{
                                              verticalAlign: "middle",
                                              maxWidth: "22rem",
                                            }}
                                          >
                                            <CallLogNotesCell
                                              call={call}
                                              followup={fu}
                                            />
                                          </td>
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <div className="small text-muted">
                              No call rows for this group.
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </Table>
            {loading && rows.length > 0 && (
              <div
                className="text-center py-2 border-top small"
                style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
              >
                <Spinner size="sm" className="me-2" style={{ color: "#0d6efd" }} />
                Updating page…
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter
        className="d-flex flex-wrap align-items-center justify-content-between gap-2 border-top"
        style={{ backgroundColor: "#f8f9fa" }}
      >
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {showPager && (
            <>
              <span className="small" style={{ color: "#495057" }}>
                Page <strong style={{ color: "#212529" }}>{page}</strong> of{" "}
                <strong style={{ color: "#212529" }}>{last}</strong>
                <span style={{ color: "#212529" }}> · {total} groups</span>
              </span>
              {loading && (
                <Spinner size="sm" style={{ color: "#0d6efd" }} className="ms-1" />
              )}
              <Button
                color="secondary"
                outline
                size="sm"
                className="border-secondary text-dark"
                disabled={!canPrev}
                onClick={() => fetchHistory(page - 1)}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                outline
                size="sm"
                className="border-secondary text-dark"
                disabled={!canNext}
                onClick={() => fetchHistory(page + 1)}
              >
                Next
              </Button>
            </>
          )}
        </div>
        <Button color="secondary" outline className="border-secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

CallLogHistoryDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  summaryLog: PropTypes.object,
  filterStartDate: PropTypes.string,
  filterEndDate: PropTypes.string,
  directionFilter: PropTypes.string,
  onDirectionFilterChange: PropTypes.func,
}

export default CallLogHistoryDetailModal
